import React, { useState, useEffect } from "react";
import { CCard, CCardHeader, CCardBody, CRow, CCol, CButton, CSpinner, CBadge, CProgress } from "@coreui/react";
import { CChartBar, CChartDoughnut, CChartLine, CChartPie, CChartPolarArea, CChartRadar } from "@coreui/react-chartjs";
import CIcon from "@coreui/icons-react";
import { cilCalendar, cilChart } from "@coreui/icons";
import { DateRangePicker } from "react-date-range";
import Select from "react-select";
import { format, subMonths } from "date-fns";
import { useSearchParams } from "react-router-dom";
import { getSMDSummary } from "src/services/Analytics";
import { getSalesList } from "src/services/SMD";
import Moment from "moment"
import DataTable from "src/components/DataTable";
import DataTablePagination from "src/components/DataTablePagination";
import { useNavigate } from "react-router-dom";
import { cilFilter } from "@coreui/icons";
import { 
  CModal, 
  CModalHeader, 
  CModalTitle, 
  CModalBody, 
  CModalFooter,
  CForm,
  CFormLabel,
  CFormInput,
  CFormSelect
} from "@coreui/react";
import moment from "moment";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap, Tooltip } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const SMDAnalytics = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [loadingSales, setLoadingSales] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [salesOptions, setSalesOptions] = useState([]);
  const [selectedSales, setSelectedSales] = useState(null);
  const navigate = useNavigate();

  // Date range state
  const initialStartDate = searchParams.get("startDate")
    ? new Date(searchParams.get("startDate"))
    : new Date(Moment(new Date()).startOf("month"));
  const initialEndDate = searchParams.get("endDate") ? new Date(searchParams.get("endDate")) : new Date();
  const initialSalesID = searchParams.get("salesId") || null;
  const initialDuration = searchParams.get("duration") || "";
  const [dateRange, setDateRange] = useState([{ startDate: initialStartDate, endDate: initialEndDate }]);
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Add these state variables after the existing state declarations (around line 30)
  // Update the filter state to use time ranges
  const [visitMapFilters, setVisitMapFilters] = useState({
    order: "",
    storeName: "",
    type: "",
    checkInTimeFrom: "", // Start time for check-in range
    checkInTimeTo: "",   // End time for check-in range
    checkOutTimeFrom: "", // Start time for check-out range
    checkOutTimeTo: "",   // End time for check-out range
    checkInDateFrom: "",
    checkInDateTo: "",
  });
  const [filteredVisitMap, setFilteredVisitMap] = useState([]);
  const [visitMapFilterOpen, setVisitMapFilterOpen] = useState(false);

  // Add these new state variables for individual section date filters
  const [topStoresDateRange, setTopStoresDateRange] = useState([
    { startDate: initialStartDate, endDate: initialEndDate }
  ]);
  const [topStockedProductsDateRange, setTopStockedProductsDateRange] = useState([
    { startDate: initialStartDate, endDate: initialEndDate }
  ]);
  const [topOutOfStockProductsDateRange, setTopOutOfStockProductsDateRange] = useState([
    { startDate: initialStartDate, endDate: initialEndDate }
  ]);

  const [showTopStoresDatePicker, setShowTopStoresDatePicker] = useState(false);
  const [showTopStockedProductsDatePicker, setShowTopStockedProductsDatePicker] = useState(false);
  const [showTopOutOfStockProductsDatePicker, setShowTopOutOfStockProductsDatePicker] = useState(false);

  // Helper: ambil posisi [lat, lng] dari object visit
  const getVisitPosition = (visit) => {
    const lat = visit.latitude ?? visit.lat ?? visit.store_latitude ?? visit.store_lat;
    const lng = visit.longitude ?? visit.lon ?? visit.lng ?? visit.store_longitude ?? visit.store_lng ?? visit.store_lon;
    if (lat != null && lng != null) {
      const la = parseFloat(lat), lo = parseFloat(lng);
      if (!Number.isNaN(la) && !Number.isNaN(lo)) return [la, lo];
    }
    return null;
  };

  // Helper: komponen untuk auto-fit bounds ke semua posisi
  const FitBoundsOnPositions = ({ positions }) => {
    const map = useMap();
    useEffect(() => {
      if (positions?.length > 1) {
        const bounds = L.latLngBounds(positions);
        map.fitBounds(bounds, { padding: [20, 20] });
      } else if (positions?.length === 1) {
        map.setView(positions[0], 15);
      }
    }, [positions, map]);
    return null;
  };

  // Helper icon urutan
  const getOrderIcon = (order, isStart, isEnd) => {
    const bg = isStart ? "#28a745" : isEnd ? "#dc3545" : "#0d6efd";
    const size = 30;
    return L.divIcon({
      className: "visit-order-icon",
      html: `
        <div style="
          width:${size}px;height:${size}px;border-radius:50%;
          background:${bg};color:#fff;display:flex;align-items:center;justify-content:center;
          font-weight:700;font-size:14px;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.25);
        ">
          ${order ?? "?"}
        </div>
      `,
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
      popupAnchor: [0, -size / 2],
    });
  };

  // Fetch sales people
  useEffect(() => {
    const fetchSalesPeople = async () => {
      setLoadingSales(true);
      try {
        const res = await getSalesList();
        const options = res.data.map((sales) => ({
          value: sales.id,
          label: sales.name,
        }));
        setSalesOptions(options);

        // Set initial selected sales from URL if available
        if (initialSalesID) {
          const parsed = parseInt(initialSalesID);
          const matched = options.find((opt) => opt.value === parsed);
          if (matched) setSelectedSales(matched);
        }
      } catch (error) {
        console.error("Error loading sales people:", error);
      } finally {
        setLoadingSales(false);
      }
    };

    fetchSalesPeople();
  }, []);

  // Fetch analytics data
  useEffect(() => {
    const fetchData = async () => {
      if (!selectedSales) return;

      setLoading(true);
      try {
        const startDate = format(dateRange[0].startDate, "yyyy-MM-dd");
        const endDate = format(dateRange[0].endDate, "yyyy-MM-dd");

        const res = await getSMDSummary({
          smdId: selectedSales.value,
          dateFrom: startDate,
          dateTo: endDate,
        });

        if (res.success) {
          setAnalyticsData(res.data);
        }
      } catch (error) {
        console.error("Error fetching analytics:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedSales, dateRange]);

  // Add this useEffect after the existing useEffect hooks (around line 80)
  useEffect(() => {
    if (analyticsData?.visit_map) {
      applyVisitMapFilters();
    }
  }, [analyticsData?.visit_map, visitMapFilters]);

  const handleSalesChange = (selectedOption) => {
    setSelectedSales(selectedOption);
    // Update URL param
    const params = {};
    searchParams.forEach((value, key) => (params[key] = value));
    if (selectedOption) {
      params.salesId = selectedOption.value;
    } else {
      delete params.salesId;
    }
    setSearchParams(params);
  };

  const handleDateApply = () => {
    setShowDatePicker(false);
    // Update URL params
    const params = {};
    searchParams.forEach((value, key) => (params[key] = value));
    params.startDate = format(dateRange[0].startDate, "yyyy-MM-dd");
    params.endDate = format(dateRange[0].endDate, "yyyy-MM-dd");
    setSearchParams(params);
  };

  // Helper to render percentage bars
  const renderPercentageBar = (value) => (
    <div className="progress-container">
      <CProgress value={value} max={100} color={value >= 80 ? "success" : value >= 50 ? "warning" : "danger"} />
      <span className="progress-value">{value}%</span>
    </div>
  );

  const handleBarClick = (idx) => {
    const trendItem = analyticsData.performance_trend[idx];
    if (!trendItem) return;
    const monthLabel = trendItem.month; // e.g., "2024-05"
    const [year, month] = monthLabel.split('-').map(Number);

    // First day of the month
    const firstDay = new Date(year, month - 1, 1);

    // Last day of the month
    const lastDay = new Date(year, month, 0);

    // Format as YYYY-MM-DD
    const startDate = firstDay.toISOString().slice(0, 10);
    const endDate = lastDay.toISOString().slice(0, 10);

    const smdId = selectedSales?.value;

    navigate(`/visitations?startDate=${startDate}&endDate=${endDate}&salesID=${smdId}`);
  };

  const handleBarVisibility = (idx) => {
    // Apply the same sorting logic as in the chart
    const order = ["Low", "Medium", "High"];
    const sorted = (analyticsData.visit_stats || [])
      .slice()
      .sort(
        (a, b) =>
          order.indexOf(a.visibility) -
          order.indexOf(b.visibility)
      );
    
    const visibilityItem = sorted[idx]; // Use sorted array instead
    if (!visibilityItem) return;
    const label = visibilityItem.visibility;
    const smdId = selectedSales?.value;

    navigate(`/visitations?visibility=${label}&salesID=${smdId}&startDate=${format(dateRange[0].startDate, "yyyy-MM-dd")}&endDate=${format(dateRange[0].endDate, "yyyy-MM-dd")}`);    
  }

  // Add these filter functions after the existing helper functions (around line 140)
  const applyVisitMapFilters = () => {
    if (!analyticsData?.visit_map) return;
    
    let filtered = [...analyticsData.visit_map];
    
    if (visitMapFilters.storeName) {
      filtered = filtered.filter(visit => 
        visit.store_name.toLowerCase().includes(visitMapFilters.storeName.toLowerCase())
      );
    }
    
    // Filter by check-in time range
    if (visitMapFilters.checkInTimeFrom || visitMapFilters.checkInTimeTo) {
      filtered = filtered.filter(visit => {
        if (!visit.check_in) return false;
        
        const visitTime = visit.check_in;
        const fromTime = visitMapFilters.checkInTimeFrom;
        const toTime = visitMapFilters.checkInTimeTo;
        
        if (fromTime && toTime) {
          return visitTime >= fromTime && visitTime <= toTime;
        } else if (fromTime) {
          return visitTime >= fromTime;
        } else if (toTime) {
          return visitTime <= toTime;
        }
        return true;
      });
    }
    
    // Filter by check-out time range
    if (visitMapFilters.checkOutTimeFrom || visitMapFilters.checkOutTimeTo) {
      filtered = filtered.filter(visit => {
        if (!visit.check_out) return false;
        
        const visitTime = visit.check_out;
        const fromTime = visitMapFilters.checkOutTimeFrom;
        const toTime = visitMapFilters.checkOutTimeTo;
        
        if (fromTime && toTime) {
          return visitTime >= fromTime && visitTime <= toTime;
        } else if (fromTime) {
          return visitTime >= fromTime;
        } else if (toTime) {
          return visitTime <= toTime;
        }
        return true;
      });
    }

    // Fix the created_at date filter
    if (visitMapFilters.checkInDateFrom || visitMapFilters.checkInDateTo) {
      filtered = filtered.filter(visit => {
        if (!visit.created_at) return false;
        
        const visitDate = new Date(visit.created_at);
        const fromDate = visitMapFilters.checkInDateFrom ? new Date(visitMapFilters.checkInDateFrom) : null;
        const toDate = visitMapFilters.checkInDateTo ? new Date(visitMapFilters.checkInDateTo + 'T23:59:59') : null;
        
        if (fromDate && toDate) {
          return visitDate >= fromDate && visitDate <= toDate;
        } else if (fromDate) {
          return visitDate >= fromDate;
        } else if (toDate) {
          return visitDate <= toDate;
        }
        return true;
      });
    }
    
    if (visitMapFilters.order) {
      filtered = filtered.filter(visit => 
        visit.order.toString().includes(visitMapFilters.order)
      );
    }

    if (visitMapFilters.type) {
      filtered = filtered.filter(visit => 
        visit.type.toString().includes(visitMapFilters.type)
      );
    }

    setFilteredVisitMap(filtered);
  };

  const handleVisitMapFilterChange = (name, value) => {
    setVisitMapFilters(prev => ({ ...prev, [name]: value }));
  };

  const resetVisitMapFilters = () => {
    setVisitMapFilters({
      order: "",
      storeName: "",
      type: "",
      checkInTimeFrom: "",
      checkInTimeTo: "",
      checkOutTimeFrom: "",
      checkOutTimeTo: "",
      checkInDateFrom: "",
      checkInDateTo: "",
    });
  };

  // Add this function to constrain individual date ranges to main date range
  const constrainDateRange = (dateRange, mainStartDate, mainEndDate) => {
    const startDate = new Date(Math.max(dateRange.startDate, mainStartDate));
    const endDate = new Date(Math.min(dateRange.endDate, mainEndDate));
    return { startDate, endDate };
  };

  // Update the individual date range change handlers to respect main date bounds
  const handleTopStoresDateRangeChange = (item) => {
    const constrained = constrainDateRange(
      item.range1, 
      dateRange[0].startDate, 
      dateRange[0].endDate
    );
    setTopStoresDateRange([constrained]);
  };

  const handleTopStockedProductsDateRangeChange = (item) => {
    const constrained = constrainDateRange(
      item.range1, 
      dateRange[0].startDate, 
      dateRange[0].endDate
    );
    setTopStockedProductsDateRange([constrained]);
  };

  const handleTopOutOfStockProductsDateRangeChange = (item) => {
    const constrained = constrainDateRange(
      item.range1, 
      dateRange[0].startDate, 
      dateRange[0].endDate
    );
    setTopOutOfStockProductsDateRange([constrained]);
  };

  // Add these new handler functions for individual date pickers
  const handleTopStoresDateApply = () => {
    setShowTopStoresDatePicker(false);
    // The data will be filtered in the render based on topStoresDateRange
  };

  const handleTopStockedProductsDateApply = () => {
    setShowTopStockedProductsDatePicker(false);
    // The data will be filtered in the render based on topStockedProductsDateRange
  };

  const handleTopOutOfStockProductsDateApply = () => {
    setShowTopOutOfStockProductsDatePicker(false);
    // The data will be filtered in the render based on topOutOfStockProductsDateRange
  };

  // Add these filter functions to get filtered data for each section
  const getFilteredTopStores = () => {
    if (!analyticsData?.top_stores) return [];
    
    const startDate = topStoresDateRange[0].startDate;
    const endDate = topStoresDateRange[0].endDate;
    
    return analyticsData.top_stores.filter(store => {
      if (!store.last_visited) return true; // Include if no date
      const storeDate = new Date(store.last_visited);
      return storeDate >= startDate && storeDate <= endDate;
    });
  };

  const getFilteredTopStockedProducts = () => {
    if (!analyticsData?.top_stocked_products) return [];
    
    const startDate = topStockedProductsDateRange[0].startDate;
    const endDate = topStockedProductsDateRange[0].endDate;
    
    return analyticsData.top_stocked_products.filter(product => {
      if (!product.created_at) return true; // Include if no date
      const productDate = new Date(product.created_at);
      return productDate >= startDate && productDate <= endDate;
    });
  };

  const getFilteredTopOutOfStockProducts = () => {
    if (!analyticsData?.top_out_of_stock_products) return [];
    
    const startDate = topOutOfStockProductsDateRange[0].startDate;
    const endDate = topOutOfStockProductsDateRange[0].endDate;
    
    return analyticsData.top_out_of_stock_products.filter(product => {
      if (!product.created_at) return true; // Include if no date
      const productDate = new Date(product.created_at);
      return productDate >= startDate && productDate <= endDate;
    });
  };

  return (
    <div className="smd-analytics">
      <CCard>
        <CCardHeader>
          <CRow className="align-items-center">
            <CCol xs={12} md={6} className="mb-3 mb-md-0">
              <h4 className="mb-0 d-flex align-items-center">
                <CIcon icon={cilChart} className="me-2" />
                SMD Performance Analytics
              </h4>
            </CCol>

            <CCol xs={12} md={6}>
              <div className="d-flex flex-column flex-md-row justify-content-md-end align-items-md-center gap-2">
                <div style={{ minWidth: "220px" }}>
                  <Select
                    options={salesOptions}
                    value={selectedSales}
                    onChange={handleSalesChange}
                    placeholder="Select SMD"
                    isClearable
                    isLoading={loadingSales}
                  />
                </div>

                <CButton
                  color="primary"
                  onClick={() => setShowDatePicker(!showDatePicker)}
                  disabled={!selectedSales}
                >
                  <CIcon icon={cilCalendar} className="me-2" />
                  {format(dateRange[0].startDate, "MMM d, yyyy")} –{" "}
                  {format(dateRange[0].endDate, "MMM d, yyyy")}
                </CButton>
              </div>
            </CCol>
          </CRow>
        </CCardHeader>

        {showDatePicker && (
          <CCardBody className="date-picker-container">
            <DateRangePicker
              onChange={(item) =>
                setDateRange([
                  {
                    startDate: item.range1.startDate,
                    endDate: item.range1.endDate,
                  },
                ])
              }
              showSelectionPreview={true}
              moveRangeOnFirstSelection={false}
              months={2}
              ranges={dateRange}
              direction="horizontal"
            />
            <div className="text-right mt-3">
              <CButton
                color="secondary"
                onClick={() => setShowDatePicker(false)}
                className="mr-2"
              >
                Cancel
              </CButton>
              <CButton color="primary" onClick={handleDateApply}>
                Apply
              </CButton>
            </div>
          </CCardBody>
        )}

        {loading ? (
          <CCardBody className="text-center">
            <CSpinner />
            <p className="mt-2">Loading analytics data...</p>
          </CCardBody>
        ) : analyticsData && selectedSales ? (
          <CCardBody>
            {/* Summary Cards */}
            <CRow className="mb-4">
              <CCol sm={6} lg={3}>
                <CCard className="text-white bg-info h-100">
                  <CCardBody>
                    <div className="text-value">
                      {analyticsData.summary.total_assigned_stores || 0}
                    </div>
                    <div>Assigned Stores</div>
                  </CCardBody>
                </CCard>
              </CCol>

              <CCol sm={6} lg={3}>
                <CCard className="text-white bg-success h-100">
                  <CCardBody>
                    <div className="text-value">
                      {analyticsData.summary.total_stores_visited || 0}
                    </div>
                    <div>Visited Stores</div>
                  </CCardBody>
                </CCard>
              </CCol>

              <CCol sm={6} lg={3}>
                <CCard className="h-100">
                  <CCardBody>
                    <div>Coverage Rate</div>
                    {renderPercentageBar(
                      analyticsData.summary.coverage_percentage || 0
                    )}
                  </CCardBody>
                </CCard>
              </CCol>

              <CCol sm={6} lg={3}>
                <CCard className="h-100">
                  <CCardBody>
                    <div className="text-value">
                      {analyticsData.summary.avg_visit_duration
                        ? Math.round(analyticsData.summary.avg_visit_duration)
                        : 0}{" "}
                      mins
                    </div>
                    <div>Avg Visit Duration</div>
                  </CCardBody>
                </CCard>
              </CCol>
            </CRow>

            {/* Charts Row */}
            <CRow className="mb-4">
              <CCol lg={6}>
                <CCard>
                  <CCardHeader>
                    <h5>Visit Statistics by Visibility</h5>
                  </CCardHeader>
                  <CCardBody>
                    <CChartBar
                      data={(() => {
                        const order = ["Low", "Medium", "High"];
                        const sorted = (analyticsData.visit_stats || [])
                          .slice()
                          .sort(
                            (a, b) =>
                              order.indexOf(a.visibility) -
                              order.indexOf(b.visibility)
                          );
                        return {
                          labels: sorted.map((item) => item.visibility),
                          datasets: [
                            {
                              label: "Visit Count",
                              backgroundColor: "#4e5d97",
                              data: sorted.map((item) => item.visit_count),
                            },
                            {
                              label: "Avg Duration (mins)",
                              backgroundColor: "#39f",
                              data: sorted.map((item) =>
                                Math.round(item.avg_duration)
                              ),
                            },
                          ],
                        };
                      })()}
                      options={{
                        aspectRatio: 1.5,
                        scales: {
                          yAxes: [
                            {
                              ticks: {
                                beginAtZero: true,
                              },
                            },
                          ],
                        },
                        onClick: (evt, elements) => {
                          if (elements && elements.length > 0) {
                            const idx = elements[0].index;
                            handleBarVisibility(idx);
                          }
                        },
                      }}
                    />
                  </CCardBody>
                </CCard>
              </CCol>

              <CCol lg={6}>
                <CCard>
                  <CCardHeader>
                    <h5>Performance Trend (Last 6 Months)</h5>
                  </CCardHeader>
                  <CCardBody>
                    <CChartBar
                      data={{
                        labels: analyticsData.performance_trend.map(
                          (item) => item.month
                        ),
                        datasets: [
                          {
                            label: "Unique Stores Visited",
                            backgroundColor: "rgba(78, 93, 151, 1)",
                            borderColor: "#4e5d97",
                            pointBackgroundColor: "#4e5d97",
                            pointHoverBackgroundColor: "#4e5d97",
                            data: analyticsData.performance_trend.map(
                              (item) => item.unique_stores_visited
                            ),
                          },
                          {
                            label: "Total Visits",
                            backgroundColor: "rgba(57, 153, 255, 1)",
                            borderColor: "#39f",
                            pointBackgroundColor: "#39f",
                            pointHoverBackgroundColor: "#39f",
                            data: analyticsData.performance_trend.map(
                              (item) => item.total_visits
                            ),
                          },
                        ],
                      }}
                      options={{
                        aspectRatio: 1.5,
                        scales: {
                          yAxes: [
                            {
                              ticks: {
                                beginAtZero: true,
                              },
                            },
                          ],
                        },
                        onClick: (evt, elements) => {
                          if (elements && elements.length > 0) {
                            const idx = elements[0].index;
                            handleBarClick(idx);
                          }
                        },
                      }}
                    />
                  </CCardBody>
                </CCard>
              </CCol>
            </CRow>

            {/* Store Coverage */}
            <CRow className="mb-4 d-flex justify-content-center">
              <CCol>
                <CCard>
                  <CCardHeader>
                    <h5>Store Coverage by Type</h5>
                  </CCardHeader>
                  <CCardBody>
                    {analyticsData?.store_coverage?.length > 0 ? (
                      analyticsData.store_coverage.map((item, index) => (
                        <div
                          key={item.type}
                          className="mb-3"
                          style={{ cursor: "pointer" }}
                          onClick={() =>
                            navigate(
                              `/stores?smdId=${
                                selectedSales?.value
                              }&type=${encodeURIComponent(item.type)}`
                            )
                          }
                        >
                          <div className="d-flex justify-content-between align-items-center">
                            <span>
                              <CBadge color="primary" className="me-2">
                                {index + 1}
                              </CBadge>
                              Type {item.type}
                            </span>
                            <span>
                              <CBadge color="info" className="me-2">
                                {item.total_stores} stores
                              </CBadge>
                              <CBadge color="success" className="me-2">
                                {item.visited_stores} visited
                              </CBadge>
                              <CBadge color="secondary">
                                {item.coverage_percentage}% coverage
                              </CBadge>
                            </span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center text-muted py-4">
                        No data available
                      </div>
                    )}
                  </CCardBody>
                </CCard>
              </CCol>
            </CRow>

            {/* Top 10 Visited Stores */}
            <CRow className="mb-4">
              <CCol>
                <CCard>
                  <CCardHeader>
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
                      <h5 className="mb-0">Top 10 Visited Stores</h5>
                      <div className="d-flex gap-2">
                        <CButton
                          color="primary"
                          size="sm"
                          onClick={() => setShowTopStoresDatePicker(!showTopStoresDatePicker)}
                        >
                          <CIcon icon={cilCalendar} className="me-1" />
                          {format(topStoresDateRange[0].startDate, "MMM d")} – {format(topStoresDateRange[0].endDate, "MMM d, yyyy")}
                        </CButton>
                      </div>
                    </div>
                  </CCardHeader>
                   
                  {showTopStoresDatePicker && (
                    <CCardBody className="date-picker-container p-3">
                      <DateRangePicker
                        onChange={handleTopStoresDateRangeChange}
                        showSelectionPreview={true}
                        moveRangeOnFirstSelection={false}
                        months={1}
                        ranges={topStoresDateRange}
                        direction="horizontal"
                        className="compact-date-picker"
                      />
                      <div className="text-right mt-2">
                        <CButton
                          color="secondary"
                          size="sm"
                          onClick={() => setShowTopStoresDatePicker(false)}
                          className="mr-2"
                        >
                          Cancel
                        </CButton>
                        <CButton color="primary" size="sm" onClick={handleTopStoresDateApply}>
                          Apply
                        </CButton>
                      </div>
                    </CCardBody>
                  )}

                  <CCardBody>
                    {getFilteredTopStores().length > 0 ? (
                      getFilteredTopStores()
                        .slice(0, 10)
                        .map((store, index) => (
                          <a
                            href={`/stores/details/${store.store_id}`}
                            key={store.store_id}
                            className="text-decoration-none text-dark"
                          >
                            <div className="mb-3">
                              <div className="d-flex justify-content-between">
                                <span>
                                  <CBadge color="primary" className="me-2">
                                    {index + 1}
                                  </CBadge>
                                  {store.store_name}
                                </span>
                                <span>
                                  <CBadge color="info" className="me-2">
                                    {store.visit_count} visits
                                  </CBadge>
                                  <CBadge color="secondary" className="me-2">
                                    {store.avg_duration
                                      ? Math.round(store.avg_duration)
                                      : 0}{" "}
                                    mins
                                  </CBadge>
                                  <CBadge color="success">
                                    {store.last_visited
                                      ? store.last_visited.split("T")[0]
                                      : "N/A"}
                                  </CBadge>
                                </span>
                              </div>
                            </div>
                          </a>
                        ))
                    ) : (
                      <div className="text-center text-muted py-4">
                        No data available
                      </div>
                    )}
                  </CCardBody>
                </CCard>
              </CCol>
            </CRow>

            {/* Top 10 Stocked Products */}
            <CRow className="mb-4">
              <CCol md={6}>
                <CCard>
                  <CCardHeader>
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
                      <h5 className="mb-0">Top 10 Stocked Products</h5>
                      <div className="d-flex gap-2">
                        <CButton
                          color="primary"
                          size="sm"
                          onClick={() => setShowTopStockedProductsDatePicker(!showTopStockedProductsDatePicker)}
                        >
                          <CIcon icon={cilCalendar} className="me-1" />
                          {format(topStockedProductsDateRange[0].startDate, "MMM d")} – {format(topStockedProductsDateRange[0].endDate, "MMM d, yyyy")}
                        </CButton>
                      </div>
                    </div>
                  </CCardHeader>

                  {showTopStockedProductsDatePicker && (
                    <CCardBody className="date-picker-container p-3">
                      <DateRangePicker
                        onChange={handleTopStockedProductsDateRangeChange}
                        showSelectionPreview={true}
                        moveRangeOnFirstSelection={false}
                        months={1}
                        ranges={topStockedProductsDateRange}
                        direction="horizontal"
                        className="compact-date-picker"
                      />
                      <div className="text-right mt-2">
                        <CButton
                          color="secondary"
                          size="sm"
                          onClick={() => setShowTopStockedProductsDatePicker(false)}
                          className="mr-2"
                        >
                          Cancel
                        </CButton>
                        <CButton color="primary" size="sm" onClick={handleTopStockedProductsDateApply}>
                          Apply
                        </CButton>
                      </div>
                    </CCardBody>
                  )}

                  <CCardBody>
                    {getFilteredTopStockedProducts().length > 0 ? (
                      getFilteredTopStockedProducts()
                        .slice(0, 10)
                        .map((stock, index) => (
                          <a
                            href={`/product/details/${stock.product_id}`}
                            key={stock.product_id}
                            className="text-decoration-none text-dark"
                          >
                            <div className="mb-3">
                              <div className="d-flex justify-content-between">
                                <span>
                                  <CBadge color="primary" className="me-2">
                                    {index + 1}
                                  </CBadge>
                                  {stock.product_name}
                                </span>
                                <CBadge color="secondary">
                                  {stock.total_stock} stores
                                </CBadge>
                              </div>
                            </div>
                          </a>
                        ))
                    ) : (
                      <div className="text-center text-muted py-4">
                        No data available
                      </div>
                    )}
                  </CCardBody>
                </CCard>
              </CCol>

              {/* Top 10 Out of Stock Products */}
              <CCol md={6}>
                <CCard>
                  <CCardHeader>
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
                      <h5 className="mb-0">Top 10 Out of Stock Products</h5>
                      <div className="d-flex gap-2">
                        <CButton
                          color="primary"
                          size="sm"
                          onClick={() => setShowTopOutOfStockProductsDatePicker(!showTopOutOfStockProductsDatePicker)}
                        >
                          <CIcon icon={cilCalendar} className="me-1" />
                          {format(topOutOfStockProductsDateRange[0].startDate, "MMM d")} – {format(topOutOfStockProductsDateRange[0].endDate, "MMM d, yyyy")}
                        </CButton>
                      </div>
                    </div>
                  </CCardHeader>

                  {showTopOutOfStockProductsDatePicker && (
                    <CCardBody className="date-picker-container p-3">
                      <DateRangePicker
                        onChange={handleTopOutOfStockProductsDateRangeChange}
                        showSelectionPreview={true}
                        moveRangeOnFirstSelection={false}
                        months={1}
                        ranges={topOutOfStockProductsDateRange}
                        direction="horizontal"
                        className="compact-date-picker"
                      />
                      <div className="text-right mt-2">
                        <CButton
                          color="secondary"
                          size="sm"
                          onClick={() => setShowTopOutOfStockProductsDatePicker(false)}
                          className="mr-2"
                        >
                          Cancel
                        </CButton>
                        <CButton color="primary" size="sm" onClick={handleTopOutOfStockProductsDateApply}>
                          Apply
                        </CButton>
                      </div>
                    </CCardBody>
                  )}

                  <CCardBody>
                    {getFilteredTopOutOfStockProducts().length > 0 ? (
                      getFilteredTopOutOfStockProducts()
                        .slice(0, 10)
                        .map((noStock, index) => (
                          <a
                            href={`/product/details/${noStock.product_id}`}
                            key={noStock.product_id}
                            className="text-decoration-none text-dark"
                          >
                            <div className="mb-3">
                              <div className="d-flex justify-content-between">
                                <span>
                                  <CBadge color="primary" className="me-2">
                                    {index + 1}
                                  </CBadge>
                                  {noStock.product_name}
                                </span>
                                <CBadge color="secondary">
                                  {noStock.total_stock} stores
                                </CBadge>
                              </div>
                            </div>
                          </a>
                        ))
                    ) : (
                      <div className="text-center text-muted py-4">
                        No data available
                      </div>
                    )}
                  </CCardBody>
                </CCard>
              </CCol>
            </CRow>

            {/* Map Daftar Kunjungan (Visit Map) */}
            <CRow className="mb-4">
              <CCol>
                <CCard>
                  <CCardHeader>
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
                      <h5 className="mb-0">
                        Daftar Kunjungan (Urutan Kunjungan)
                      </h5>
                      <div className="d-flex gap-2">
                        <CButton
                          color="primary"
                          size="sm"
                          onClick={() => setVisitMapFilterOpen(true)}
                        >
                          <CIcon icon={cilFilter} className="me-1" />
                          Filter
                        </CButton>
                        <CButton
                          color="danger"
                          size="sm"
                          onClick={resetVisitMapFilters}
                        >
                          Reset
                        </CButton>
                      </div>
                    </div>

                    {/* Active Filters Display */}
                    {Object.values(visitMapFilters).some((value) => value) && (
                      <div className="mt-3">
                        <small className="text-muted me-2">
                          Active Filters:
                        </small>
                        {Object.entries(visitMapFilters).map(([key, value]) => {
                          if (!value) return null;

                          // Handle date range display
                          if (
                            key === "checkInDateFrom" &&
                            visitMapFilters.checkInDateTo
                          ) {
                            return (
                              <CBadge
                                key={key}
                                color="primary"
                                className="me-2 mb-1"
                                shape="rounded-pill"
                              >
                                Date Range: {value} -{" "}
                                {visitMapFilters.checkInDateTo}
                              </CBadge>
                            );
                          }

                          // Handle time range display
                          if (
                            key === "checkInTimeFrom" &&
                            visitMapFilters.checkInTimeTo
                          ) {
                            return (
                              <CBadge
                                key={key}
                                color="primary"
                                className="me-2 mb-1"
                                shape="rounded-pill"
                              >
                                Check-in Time: {value} -{" "}
                                {visitMapFilters.checkInTimeTo}
                              </CBadge>
                            );
                          }

                          if (
                            key === "checkOutTimeFrom" &&
                            visitMapFilters.checkOutTimeTo
                          ) {
                            return (
                              <CBadge
                                key={key}
                                color="primary"
                                className="me-2 mb-1"
                                shape="rounded-pill"
                              >
                                Check-out Time: {value} -{" "}
                                {visitMapFilters.checkOutTimeTo}
                              </CBadge>
                            );
                          }

                          // Skip the "to" fields as they're handled above
                          if (
                            key === "checkInTimeTo" ||
                            key === "checkOutTimeTo" ||
                            key === "checkInDateTo"
                          )
                            return null;

                          // Handle single date value
                          if (
                            key === "checkInDateFrom" &&
                            !visitMapFilters.checkInDateTo
                          ) {
                            return (
                              <CBadge
                                key={key}
                                color="primary"
                                className="me-2 mb-1"
                                shape="rounded-pill"
                              >
                                Date: {value}+
                              </CBadge>
                            );
                          }

                          // Handle single time values
                          if (
                            key === "checkInTimeFrom" &&
                            !visitMapFilters.checkInTimeTo
                          ) {
                            return (
                              <CBadge
                                key={key}
                                color="primary"
                                className="me-2 mb-1"
                                shape="rounded-pill"
                              >
                                Check-in Time: {value}+
                              </CBadge>
                            );
                          }

                          if (
                            key === "checkOutTimeFrom" &&
                            !visitMapFilters.checkOutTimeTo
                          ) {
                            return (
                              <CBadge
                                key={key}
                                color="primary"
                                className="me-2 mb-1"
                                shape="rounded-pill"
                              >
                                Check-out Time: {value}+
                              </CBadge>
                            );
                          }

                          // Handle other fields
                          return (
                            <CBadge
                              key={key}
                              color="primary"
                              className="me-2 mb-1"
                              shape="rounded-pill"
                            >
                              {key === "storeName" ? "Store" : 
                               key === "order" ? "Order" : 
                               key === "type" ? "Type" : key}: {value}
                            </CBadge>
                          );
                        })}
                      </div>
                    )}
                  </CCardHeader>
                  {/* <CCardBody> */}
                  {analyticsData?.visit_map?.length > 0 ? (
                    <>
                      {/* Map visit (urutan kunjungan) */}
                      {(() => {
                        const orderedVisits = [...filteredVisitMap].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
                        const positions = orderedVisits.map(getVisitPosition).filter(Boolean);
                        const defaultCenter = positions[0] ?? [-6.2088, 106.8456]; // fallback Jakarta

                        return positions.length > 0 ? (
                          <div style={{ height: "420px", borderRadius: "0.375rem", overflow: "hidden", position: "relative", zIndex: 0 }}>
                            <MapContainer center={defaultCenter} zoom={13} style={{ height: "100%", width: "100%", position: "relative" }}>
                              <TileLayer
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                              />
                              <FitBoundsOnPositions positions={positions} />
                              <Polyline positions={positions} color="blue" weight={3} opacity={0.8} dashArray="6 6" />

                              {orderedVisits.map((v, idx) => {
                                const pos = getVisitPosition(v);
                                if (!pos) return null;
                                const isStart = idx === 0;
                                const isEnd = idx === orderedVisits.length - 1;
                                const orderNum = v.order ?? idx + 1;

                                return (
                                  <Marker
                                    key={v.visit_id ?? `${v.store_id ?? "s"}-${idx}`}
                                    position={pos}
                                    icon={getOrderIcon(orderNum, isStart, isEnd)}
                                    eventHandlers={{
                                      click: () => {
                                        if (v.store_id) navigate(`/stores/details/${v.store_id}`);
                                      },
                                    }}
                                  >
                                    <Tooltip direction="top" offset={[0, -10]} opacity={1} permanent={false}>
                                      <div style={{ minWidth: 220 }}>
                                        <div><strong>{v.store_name ?? "-"}</strong></div>
                                        <div style={{ fontSize: 12, color: "#555" }}>
                                          {v.store_address ?? v.address ?? v.storeAddress ?? "-"}
                                        </div>
                                        {/* <div style={{ fontSize: 12 }}>
                                          <strong>Order:</strong> {v.order ?? idx + 1}
                                        </div> */}
                                        <div style={{ fontSize: 12 }}>
                                          <strong>Type:</strong> {v.type ?? "-"}
                                        </div>
                                        <div style={{ fontSize: 12 }}>
                                          <strong>Check-in:</strong> {v.check_in ?? "-"}
                                        </div>
                                        <div style={{ fontSize: 12 }}>
                                          <strong>Check-out:</strong> {v.check_out ?? "-"}
                                        </div>
                                        <div style={{ fontSize: 12 }}>
                                          <strong>Date:</strong> {v.created_at ? moment(v.created_at).format("DD MMMM YYYY") : "-"}
                                        </div>
                                      </div>
                                    </Tooltip>

                                    <Popup>
                                      <div style={{ minWidth: 180 }}>
                                        <div><strong>Order:</strong> {v.order ?? idx + 1}</div>
                                        <div><strong>Store:</strong> {v.store_name ?? "-"}</div>
                                        <div><strong>Type:</strong> {v.type ?? "-"}</div>
                                        <div><strong>Check-in:</strong> {v.check_in ?? "-"}</div>
                                        <div><strong>Check-out:</strong> {v.check_out ?? "-"}</div>
                                        <div><strong>Date:</strong> {v.created_at ? moment(v.created_at).format("DD MMMM YYYY") : "-"}</div>
                                      </div>
                                    </Popup>
                                  </Marker>
                                );
                              })}
                            </MapContainer>
                          </div>
                        ) : (
                          <div className="text-center text-muted py-4">Tidak ada koordinat kunjungan yang valid</div>
                        );
                      })()}
                    </>
                  ) : (
                    <div className="text-center text-muted py-4">
                      No visit map data available
                    </div>
                  )}
                  {/* </CCardBody> */}
                </CCard>
              </CCol>
            </CRow>

            {/* Visit Map Filter Modal */}
            <CModal
              visible={visitMapFilterOpen}
              onClose={() => setVisitMapFilterOpen(false)}
              size="lg"
            >
              <CModalHeader>
                <CModalTitle>Filter Visit Map</CModalTitle>
              </CModalHeader>
              <CModalBody>
                <CForm>
                  <CRow className="mb-3">
                    <CCol md={6}>
                      <CFormLabel>Store Name</CFormLabel>
                      <CFormInput
                        type="text"
                        name="storeName"
                        value={visitMapFilters.storeName}
                        onChange={(e) =>
                          handleVisitMapFilterChange(
                            "storeName",
                            e.target.value
                          )
                        }
                        placeholder="Filter by store name"
                      />
                    </CCol>
                    <CCol md={6}>
                      <CFormLabel>Order</CFormLabel>
                      <CFormInput
                        type="text"
                        name="order"
                        value={visitMapFilters.order}
                        onChange={(e) =>
                          handleVisitMapFilterChange("order", e.target.value)
                        }
                        placeholder="Filter by order number"
                      />
                    </CCol>
                  </CRow>

                  <CRow className="mb-3">
                    <CCol md={6}>
                      <CFormLabel>Type</CFormLabel>
                      <CFormSelect
                        name="type"
                        value={visitMapFilters.type}
                        onChange={(e) =>
                          handleVisitMapFilterChange("type", e.target.value)
                        }
                      >
                        <option value="">All Types</option>
                        <option value="A">Type A</option>
                        <option value="B">Type B</option>
                        <option value="C">Type C</option>
                      </CFormSelect>
                    </CCol>
                  </CRow>

                  <CRow className="mb-3">
                    <CCol md={6}>
                      <CFormLabel>Check-in Time Range</CFormLabel>
                      <div className="d-flex gap-2">
                        <CFormInput
                          type="time"
                          name="checkInTimeFrom"
                          value={visitMapFilters.checkInTimeFrom}
                          onChange={(e) =>
                            handleVisitMapFilterChange(
                              "checkInTimeFrom",
                              e.target.value
                            )
                          }
                          step="1"
                          placeholder="From"
                        />
                        <CFormInput
                          type="time"
                          name="checkInTimeTo"
                          value={visitMapFilters.checkInTimeTo}
                          onChange={(e) =>
                            handleVisitMapFilterChange(
                              "checkInTimeTo",
                              e.target.value
                            )
                          }
                          step="1"
                          placeholder="To"
                        />
                      </div>
                    </CCol>
                    <CCol md={6}>
                      <CFormLabel>Check-out Time Range</CFormLabel>
                      <div className="d-flex gap-2">
                        <CFormInput
                          type="time"
                          name="checkOutTimeFrom"
                          value={visitMapFilters.checkOutTimeFrom}
                          onChange={(e) =>
                            handleVisitMapFilterChange(
                              "checkOutTimeFrom",
                              e.target.value
                            )
                          }
                          step="1"
                          placeholder="From"
                        />
                        <CFormInput
                          type="time"
                          name="checkOutTimeTo"
                          value={visitMapFilters.checkOutTimeTo}
                          onChange={(e) =>
                            handleVisitMapFilterChange(
                              "checkOutTimeTo",
                              e.target.value
                            )
                          }
                          step="1"
                          placeholder="To"
                        />
                      </div>
                    </CCol>
                    <CCol md={6}>
                      <CFormLabel>Date Range</CFormLabel>
                      <div className="d-flex gap-2">
                        <CFormInput
                          type="date"
                          name="checkInDateFrom"
                          value={visitMapFilters.checkInDateFrom}
                          onChange={(e) =>
                            handleVisitMapFilterChange(
                              "checkInDateFrom",
                              e.target.value
                            )
                          }
                          placeholder="From"
                        />
                        <CFormInput
                          type="date"
                          name="checkInDateTo"
                          value={visitMapFilters.checkInDateTo}
                          onChange={(e) =>
                            handleVisitMapFilterChange(
                              "checkInDateTo",
                              e.target.value
                            )
                          }
                          placeholder="To"
                        />
                      </div>
                    </CCol>
                  </CRow>
                </CForm>
              </CModalBody>
              <CModalFooter>
                <CButton color="secondary" onClick={resetVisitMapFilters}>
                  Reset
                </CButton>
                <CButton
                  color="primary"
                  onClick={() => setVisitMapFilterOpen(false)}
                >
                  Apply Filters
                </CButton>
              </CModalFooter>
            </CModal>
          </CCardBody>
        ) : (
          <CCardBody className="text-center">
            {selectedSales ? (
              <p>No analytics data available for the selected period</p>
            ) : (
              <p>Please select an SMD to view analytics</p>
            )}
          </CCardBody>
        )}
      </CCard>
    </div>
  );
};

export default SMDAnalytics;
